import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../utils/api';
import { fmtDateTime } from '../../../../utils/region';
import Pagination, { getPageSize } from '../../../Pagination';
import DlBtn from '../../../DlBtn';
import { EntryDetailDialog } from '../../JournalBook';
import { baht, btn, btnPrimary, card, dmy, errBox, inp, note, num, pill, pillGreen, pillGrey, table, td, th } from '../../ui';
import { Loader, Stat, actBtn, chip, chipOn, emptyLine } from '../../dialog';
import { ReverseReasonDialog } from '../../reverseReason';
import type { AwCtx } from '../../ctx';
import type { FaDepRow, FaRun, TabProps } from './shared';

// ── Accounting › Fixed assets › Depreciation (batch 9 R4: COA-H8 / H10 / H12 / H15) ──────────────
// The shape of PEAK's "Run Depreciation" page, with the customer's NINE columns rather than PEAK's:
// Asset No · name · category · acquisition cost · rate / life · OPENING accumulated · charge for the
// period · CLOSING accumulated · closing NBV — grouped by category, each group folding, a total under it.
//
// ⛔ NOTHING IS CALCULATED UNTIL SOMEBODY ASKS. The customer: "a Calculate Depreciation function to
// preview the depreciation calculation before posting". The classic card ran GET /depreciation on
// mount, so the first thing anybody saw was a charge nobody had asked for; here the request leaves only
// from the button, and the reload counter re-reads a preview only once one is on the screen.
//
// Every figure is the SERVER's (services/fixedAssets.ts computeDepreciation fills the nine on every row,
// skipped or not). This file adds them up for the group lines and the total and decides nothing else:
// which asset is charged, by how much and why not are all the server's answers, and a reason is shown
// from its KEY (skippedKind), never from the English sentence that travels beside it.
//
// The period is the shell's (one picker for every pane — ctx.setPeriod); the month is asked only when
// the COMPANY charges monthly. That company setting is read on mount from GET /fixed-assets/cadence
// (a read, not a calculation — the customer wants nothing calculated until Calculate is pressed), so a
// monthly company is asked for its month before its first Calculate, not after it.

type Cadence = 'annual' | 'monthly';
type Calc = { rows: FaDepRow[]; ym: string; at: Date };
type Posted = { entryId: string; docNo: string; assets: number; amount: number; skipped: number };
/** A run taken back from the history: its number and the reversal's, both now posted. */
type Reversed = { no: string; rv: string };
type TF = (key: string, opts?: Record<string, unknown>) => string;

// Every reason the server can give, each key written in full so a grep finds it. A kind this file does
// not know yet still reads as a word ("Not charged"), never as the server's English.
const SKIP_KEY: Record<string, string> = {
  not_depreciable: 'aw_fad_skip_not_depreciable', no_accounts: 'aw_fad_skip_no_accounts', not_owned: 'aw_fad_skip_not_owned',
  already_charged: 'aw_fad_skip_already_charged', fully_depreciated: 'aw_fad_skip_fully_depreciated', not_available: 'aw_fad_skip_not_available',
};
const RUN_STATUS_KEY: Record<string, string> = { draft: 'aw_fad_status_draft', posted: 'aw_fad_status_posted' };
// Why a posted run may not be reversed (services/fixedAssets.ts runReversalRefusal), each key in full.
// A code this file does not know yet still reads as a sentence of its own, never as the server's English.
const REFUSAL_KEY: Record<string, string> = {
  closed_period: 'aw_fad_reverse_refused_closed', disposed: 'aw_fad_reverse_refused_disposed', disposal_draft: 'aw_fad_reverse_refused_disposal_draft',
};
const pillBlue: React.CSSProperties = { ...pill, color: '#1E40AF', background: '#EFF6FF', border: '1px solid #BFDBFE' };
const runPill = (s: string): React.CSSProperties => (s === 'posted' ? pillGreen : pillGrey);

/** The same rows the sheet carries (routes/fixedAssets.ts /depreciation/export): an asset gone before
 *  this window is not part of its table — unless something is still recorded against it here. */
const inTable = (r: FaDepRow) => r.skippedKind !== 'not_owned' || Number(r.periodCharge) > 0;
/** What Post would charge: the rows the server did not skip, with something to charge. */
const isDue = (r: FaDepRow) => !r.skippedKind && Number(r.amount) > 0;
const charged = (r: FaDepRow) => r.skippedKind === 'already_charged' || (Number(r.chargedThisPeriod) > 0 && !(Number(r.amount) > 0));
const sumOf = (rs: FaDepRow[], k: 'capitalisedTotal' | 'openingAccum' | 'periodCharge' | 'closingAccum' | 'closingNbv') =>
  rs.reduce((s, r) => s + (Number(r[k]) || 0), 0);
/** A rate or a life as people write it: 20, 6.67 — never 6.666667. */
const short = (n: number) => String(Math.round((Number(n) || 0) * 100) / 100);
/** The window a calculation is of: the month when the company charges monthly, else the period. */
const windowLabel = (ym: string, ctx: AwCtx) => (ym
  ? `${ym.slice(5, 7)}/${ym.slice(0, 4)}`
  : `${dmy(ctx.period?.start_date || '')} – ${dmy(ctx.period?.end_date || '')}`);

type Group = { key: string; label: string; rows: FaDepRow[] };

/** The nine-column table's figure headings. ⛔ They WRAP, unlike the house th: "Opening accumulated
 *  depreciation" on one line made the table 1,497px wide in a 1,326px pane at 1440, and the closing
 *  NBV — the column the table exists for — sat behind a sideways scroll. The figures set the width. */
const thNum: React.CSSProperties = { ...th, textAlign: 'right', whiteSpace: 'normal', verticalAlign: 'bottom' };

export function DepreciationTab({ ctx, canWrite }: TabProps) {
  const { t } = useTranslation();
  // The inner tab is this page's own view, not an address anybody links to (the pane's ?fatab= is).
  const [view, setView] = useState<'table' | 'history'>('table');
  // The calculation lives HERE, above both inner tabs, so a look at the history does not throw it away.
  const [cadence, setCadence] = useState<Cadence | ''>('');
  const [ym, setYm] = useState('');
  const [calc, setCalc] = useState<Calc | null>(null);
  const [busy, setBusy] = useState<'' | 'calc' | 'post' | 'freq'>('');
  const [err, setErr] = useState('');
  const [freqErr, setFreqErr] = useState('');
  const [posted, setPosted] = useState<Posted | null>(null);
  const [reversed, setReversed] = useState<Reversed | null>(null);
  // The window of the preview on screen ('' = the whole period), or null when there is none — what the
  // reload counter re-reads. A ref, so a bump never re-runs a calculation nobody asked for.
  const asked = useRef<string | null>(null);
  const seq = useRef(0);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  // The company's frequency, read without calculating anything.
  useEffect(() => {
    let on = true;
    api.faGetCadence(ctx.clientId).then((r: { cadence: string }) => { if (on) setCadence(r.cadence === 'monthly' ? 'monthly' : 'annual'); })
      .catch(() => { /* left unknown: the first Calculate reads it as before */ });
    return () => { on = false; };
  }, [ctx.clientId]);

  const run = useCallback(async (want: string) => {
    const my = ++seq.current;
    const mine = () => alive.current && my === seq.current;
    setBusy('calc'); setErr('');
    try {
      let d: { rows: FaDepRow[]; cadence: string } = await api.faDepreciation(ctx.clientId, ctx.periodId, want);
      let used = want;
      // A month asked of a company that charges once per period is not a window it can post: the
      // period is what Post would charge, so the period is what the preview shows.
      if (d.cadence !== 'monthly' && used) { d = await api.faDepreciation(ctx.clientId, ctx.periodId, ''); used = ''; }
      if (!mine()) return;
      const c: Cadence = d.cadence === 'monthly' ? 'monthly' : 'annual';
      setCadence(c);
      if (c === 'annual') setYm('');
      // ⛔ Monthly, and no month: the whole period is not a window this company posts — ask for the month.
      if (c === 'monthly' && !used) { asked.current = null; setCalc(null); return; }
      asked.current = used;
      setCalc({ rows: d.rows || [], ym: used, at: new Date() });
    } catch (e: any) {
      if (mine()) setErr(String(e?.message || e));
    } finally {
      if (mine()) setBusy('');
    }
  }, [ctx.clientId, ctx.periodId]);

  // After any write — here or in another pane — a preview already on the screen is calculated again, so
  // it never shows a charge that has since been made. Nothing is calculated when there is none.
  useEffect(() => { if (asked.current !== null) void run(asked.current); }, [ctx.reload]);   // eslint-disable-line react-hooks/exhaustive-deps

  const monthly = cadence === 'monthly';
  const needMonth = monthly && !ym;
  const calculate = () => { setPosted(null); setReversed(null); void run(monthly ? ym : ''); };
  // ⛔ Only reachable while nothing is in flight: the month box is disabled while busy (review b9 #25),
  // so a Calculate answer for the month before can never land under the month now in the box.
  const pickMonth = (v: string) => { setYm(v); setCalc(null); asked.current = null; setPosted(null); setReversed(null); };

  // "Reverse & recalculate" (COA-H10.3), once Run history has reversed a run and POSTED the reversal —
  // which is what gives the register back. The window that run charged is calculated again, on the
  // table: asked.current is set to it and the shell's reload counter is bumped, so the reload effect
  // above is the ONE calculation (an explicit run() beside it would race it), and every other pane
  // (the register, the reconciliation, the journal) re-reads too.
  const afterReversal = (r: FaRun, rvNo: string) => {
    // A monthly run's window is its month; a run of a company charged once per period is the period.
    // An unknown cadence asks for the month: run() falls back to the period if the company is annual.
    const want = cadence === 'annual' ? '' : String(r.windowTo || '').slice(0, 7);
    if (cadence !== 'annual') setYm(want);
    setCalc(null); setErr(''); setPosted(null);
    setReversed({ no: r.docNo, rv: rvNo });
    asked.current = want;
    setView('table');
    ctx.bump();
  };

  // ⛔ The COMPANY's setting (clients.depreciation_cadence), written at once — not a filter on this
  // table. The server refuses it once an open period carries charges; that refusal is shown as it came,
  // and the select keeps the value the company still has.
  const changeFreq = async (next: Cadence) => {
    if (next === cadence) return;
    setBusy('freq'); setFreqErr('');
    try {
      const r: { cadence: string } = await api.faCadence(ctx.clientId, next);
      if (!alive.current) return;
      setCadence(r.cadence === 'monthly' ? 'monthly' : 'annual');
      setCalc(null); asked.current = null; setYm(''); setPosted(null);
      ctx.bump();
    } catch (e: any) {
      if (alive.current) setFreqErr(String(e?.message || e));
    } finally {
      if (alive.current) setBusy('');
    }
  };

  const due = calc ? calc.rows.filter(isDue) : [];
  const dueTotal = due.reduce((s, r) => s + Number(r.amount), 0);
  // ⛔ Posts the window that was PREVIEWED (calc.ym), not whatever the month box says now — changing the
  // month clears the preview and cannot happen while a calculation is in flight, so the two cannot
  // differ, but the posting reads the one that was shown.
  const post = async () => {
    if (!calc || !dueTotal) return;
    if (!window.confirm(t('aw_fad_post_confirm', { amount: baht(dueTotal), n: due.length, window: windowLabel(calc.ym, ctx) }))) return;
    setBusy('post'); setErr(''); setPosted(null); setReversed(null);
    try {
      const r: Posted = await api.faPostDepreciation(ctx.clientId, ctx.periodId, calc.ym);
      if (!alive.current) return;
      setPosted(r);
      ctx.bump();   // the preview above is calculated again (the reload effect), and the history re-read
    } catch (e: any) {
      if (alive.current) setErr(String(e?.message || e));
    } finally {
      if (alive.current) setBusy('');
    }
  };

  const freqWord = (c: Cadence) => (c === 'monthly' ? t('aw_fad_freq_monthly') : t('aw_fad_freq_annual'));
  const minYm = String(ctx.period?.start_date || '').slice(0, 7);
  const maxYm = String(ctx.period?.end_date || '').slice(0, 7);

  const controls = (
    <div style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }} data-fad-period>
        <i className="ti ti-calendar" aria-hidden /> {t('aw_fad_period', { from: dmy(ctx.period?.start_date || ''), to: dmy(ctx.period?.end_date || '') })}
      </span>
      <span style={{ width: 1, height: 22, background: 'var(--border-strong)', flex: 'none' }} aria-hidden />
      {canWrite ? (
        <label style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }} title={t('aw_fad_freq_title')}>
          {t('aw_fad_freq_label')}
          <select value={cadence} disabled={busy !== ''} aria-label={t('aw_fad_freq_label')} data-fad-freq
            onChange={e => { void changeFreq(e.target.value === 'monthly' ? 'monthly' : 'annual'); }} style={{ ...inp, padding: '5px 8px' }}>
            {!cadence && <option value="" disabled>{t('aw_fad_freq_unknown')}</option>}
            <option value="annual">{t('aw_fad_freq_annual')}</option>
            <option value="monthly">{t('aw_fad_freq_monthly')}</option>
          </select>
        </label>
      ) : cadence ? (
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }} title={t('aw_fad_freq_title')}>{t('aw_fad_freq_is', { freq: freqWord(cadence) })}</span>
      ) : null}
      {monthly && (
        <label style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {t('aw_fad_month')}
          <input type="month" value={ym} min={minYm || undefined} max={maxYm || undefined} aria-label={t('aw_fad_month')} data-fad-month
            disabled={busy !== ''} onChange={e => pickMonth(e.target.value)} style={{ ...inp, padding: '5px 8px', opacity: busy !== '' ? 0.5 : 1 }} />
        </label>
      )}
      <button type="button" onClick={calculate} disabled={needMonth || busy !== ''} title={t('aw_fad_calc_title')} data-fad-calc
        style={{ ...btnPrimary, opacity: needMonth || busy !== '' ? 0.5 : 1 }}>
        <i className={busy === 'calc' ? 'ti ti-loader' : 'ti ti-calculator'} aria-hidden /> {t('aw_fad_calc')}
      </button>
      <span style={{ flex: 1 }} />
      {calc && (
        <DlBtn style={btn} title={t('aw_fad_excel_title')} onRun={() => api.faDepreciationExport(ctx.clientId, ctx.periodId, calc.ym)}>
          <i className="ti ti-file-spreadsheet" aria-hidden /> {t('aw_fad_excel')}
        </DlBtn>
      )}
      {canWrite && calc && dueTotal > 0 && (
        <button type="button" onClick={() => { void post(); }} disabled={busy !== ''} title={t('aw_fad_post_title')} data-fad-post
          style={{ ...btnPrimary, opacity: busy !== '' ? 0.5 : 1 }}>
          <i className={busy === 'post' ? 'ti ti-loader' : 'ti ti-file-plus'} aria-hidden /> {t('aw_fad_post', { amount: baht(dueTotal) })}
        </button>
      )}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div role="group" aria-label={t('aw_fad_view_label')} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button type="button" aria-pressed={view === 'table'} onClick={() => setView('table')} style={view === 'table' ? { ...chip, ...chipOn } : chip}>
          <i className="ti ti-table" aria-hidden /> {t('aw_fad_view_table')}
        </button>
        <button type="button" aria-pressed={view === 'history'} onClick={() => setView('history')} style={view === 'history' ? { ...chip, ...chipOn } : chip}>
          <i className="ti ti-history" aria-hidden /> {t('aw_fad_view_history')}
        </button>
      </div>
      {view === 'history' ? <RunHistory ctx={ctx} t={t} canWrite={canWrite} onReversed={afterReversal} /> : (
        <>
          {controls}
          {freqErr && <div style={errBox} data-fad-freq-err>{freqErr}</div>}
          {needMonth && <div style={{ ...card, ...note, padding: '9px 12px' }} data-fad-need-month><i className="ti ti-info-circle" aria-hidden /> {t('aw_fad_month_needed')}</div>}
          {err && <div style={errBox} data-fad-err>{err}</div>}
          {reversed && (
            <div role="status" style={{ ...card, padding: '9px 12px', display: 'flex', gap: 10, alignItems: 'center' }} data-fad-reversed-done={reversed.rv}>
              <i className="ti ti-arrow-back-up" style={{ color: '#166534' }} aria-hidden />
              <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('aw_fad_reversed_done', reversed)}</span>
            </div>
          )}
          {posted && (
            <div style={{ ...card, padding: '9px 12px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }} data-fad-posted={posted.docNo}>
              <i className={posted.docNo ? 'ti ti-circle-check' : 'ti ti-info-circle'} style={{ color: posted.docNo ? '#166534' : 'var(--text-secondary)' }} aria-hidden />
              <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', flex: 1, minWidth: 220 }}>
                {posted.docNo
                  ? t('aw_fad_posted', { no: posted.docNo, amount: baht(posted.amount), n: posted.assets })
                  : t('aw_fad_posted_none', { n: posted.skipped })}
              </span>
              {posted.docNo && (
                <button type="button" style={actBtn} onClick={() => ctx.setNav('accounting', 'journal_entries')}>
                  <i className="ti ti-arrow-right" aria-hidden /> {t('aw_fad_open_journal')}
                </button>
              )}
            </div>
          )}
          {calc ? <DepTable ctx={ctx} calc={calc} t={t} /> : busy === 'calc' ? <Loader /> : !needMonth && (
            <div style={{ ...card, ...emptyLine }} data-fad-prompt>{t('aw_fad_calc_prompt')}</div>
          )}
        </>
      )}
    </div>
  );
}

/** The nine columns, grouped by category, with the three summary blocks above them. */
function DepTable({ ctx, calc, t }: { ctx: AwCtx; calc: Calc; t: TF }) {
  const { i18n } = useTranslation();
  const [folded, setFolded] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const thai = i18n.language === 'th';
  const catName = (r: FaDepRow) => (thai ? (r.categoryTh || r.categoryEn) : (r.categoryEn || r.categoryTh)) || t('aw_fad_no_category');

  const rows = calc.rows.filter(inTable);
  // One group per category, named in the reader's language; "No category" last. Within a group the
  // server's order (the asset number) is kept.
  const byKey = new Map<string, Group>();
  for (const r of rows) {
    const k = r.categoryId || '';
    if (!byKey.has(k)) byKey.set(k, { key: k, label: catName(r), rows: [] });
    byKey.get(k)!.rows.push(r);
  }
  const groups = Array.from(byKey.values()).sort((a, b) => (a.key === '' ? 1 : 0) - (b.key === '' ? 1 : 0) || a.label.localeCompare(b.label));
  // Paged as one list (100 a page, the house's convention) — a group split across two pages carries
  // its header, with the WHOLE group's count and subtotals, on both.
  const flat = groups.flatMap(g => g.rows.map(r => ({ g, r })));
  const pages = Math.max(1, Math.ceil(flat.length / limit));
  const pg = Math.min(page, pages);
  const slice = flat.slice((pg - 1) * limit, pg * limit);
  const onPage: Group[] = [];
  for (const x of slice) if (onPage[onPage.length - 1] !== x.g) onPage.push(x.g);
  const shownOf = (g: Group) => slice.filter(x => x.g === g).map(x => x.r);

  const totals = {
    cost: sumOf(rows, 'capitalisedTotal'), open: sumOf(rows, 'openingAccum'), period: sumOf(rows, 'periodCharge'),
    close: sumOf(rows, 'closingAccum'), nbv: sumOf(rows, 'closingNbv'),
  };
  const money = (v: number) => baht(v);
  const sub: React.CSSProperties = { ...num, fontWeight: 800, background: 'var(--bg-app)' };

  return (
    <>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Stat label={t('aw_fad_stat_assets')} value={String(rows.length)} />
        <Stat label={t('aw_fad_stat_period')} value={money(totals.period)} />
        <Stat label={t('aw_fad_stat_nbv')} value={money(totals.nbv)} />
      </div>
      <div style={{ ...note, display: 'flex', alignItems: 'center', gap: 6 }} data-fad-calc-at>
        <i className="ti ti-clock" aria-hidden /> {t('aw_fad_calculated_at', { at: fmtDateTime(calc.at), window: windowLabel(calc.ym, ctx) })}
      </div>
      <div style={{ ...card, padding: 0, overflow: 'visible' }}>
        <div style={{ overflowX: 'auto' }}>
          {!rows.length ? <div style={emptyLine}>{t('aw_fad_none')}</div> : (
            <table style={table} data-fad-table>
              <thead>
                <tr>
                  <th style={th}>{t('aw_fad_col_no')}</th>
                  <th style={th}>{t('aw_fad_col_name')}</th>
                  <th style={th}>{t('aw_fad_col_category')}</th>
                  <th style={thNum}>{t('aw_fad_col_cost')}</th>
                  <th style={thNum}>{t('aw_fad_col_rate')}</th>
                  <th style={thNum}>{t('aw_fad_col_opening')}</th>
                  <th style={thNum}>{t('aw_fad_col_period')}</th>
                  <th style={thNum}>{t('aw_fad_col_closing')}</th>
                  <th style={thNum}>{t('aw_fad_col_nbv')}</th>
                </tr>
              </thead>
              <tbody>
                {onPage.map(g => {
                  const open = !folded[g.key];
                  return (
                    <React.Fragment key={g.key || '-'}>
                      <tr data-fad-group={g.key || '-'}>
                        <td colSpan={3} style={{ ...td, fontWeight: 800, background: 'var(--bg-app)' }}>
                          <button type="button" aria-expanded={open} data-fad-fold
                            aria-label={open ? t('aw_fad_fold', { name: g.label }) : t('aw_fad_unfold', { name: g.label })}
                            title={open ? t('aw_fad_fold', { name: g.label }) : t('aw_fad_unfold', { name: g.label })}
                            onClick={() => setFolded(f => ({ ...f, [g.key]: open }))}
                            style={{ background: 'none', border: 'none', padding: '0 6px 0 0', cursor: 'pointer', color: 'inherit', font: 'inherit' }}>
                            <i className={open ? 'ti ti-chevron-down' : 'ti ti-chevron-right'} aria-hidden />
                          </button>
                          <span data-fad-group-name>{g.label}</span>{' '}
                          <span style={pillBlue} title={t('aw_fad_group_count', { n: g.rows.length })} data-fad-count>{g.rows.length}</span>
                        </td>
                        <td style={sub} data-c="cost">{money(sumOf(g.rows, 'capitalisedTotal'))}</td>
                        <td style={sub} />
                        <td style={sub} data-c="open">{money(sumOf(g.rows, 'openingAccum'))}</td>
                        <td style={sub} data-c="period">{money(sumOf(g.rows, 'periodCharge'))}</td>
                        <td style={sub} data-c="close">{money(sumOf(g.rows, 'closingAccum'))}</td>
                        <td style={sub} data-c="nbv">{money(sumOf(g.rows, 'closingNbv'))}</td>
                      </tr>
                      {open && shownOf(g).map(r => (
                        <tr key={r.assetId} data-fad-asset={r.assetId} data-fad-skip={r.skippedKind || undefined} data-fad-charged={charged(r) ? '1' : undefined}>
                          <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{r.assetNo}</td>
                          <td style={td}>
                            {r.name}
                            {r.skippedKind && (
                              <div style={{ marginTop: 3 }}>
                                <span style={r.skippedKind === 'already_charged' ? pillGreen : pillGrey} data-fad-reason={r.skippedKind}>
                                  {SKIP_KEY[r.skippedKind] ? t(SKIP_KEY[r.skippedKind]) : t('aw_fad_skip_other')}
                                </span>
                              </div>
                            )}
                          </td>
                          <td style={td}>{catName(r)}</td>
                          <td style={num} data-c="cost">{money(r.capitalisedTotal)}</td>
                          <td style={num}>{Number(r.rate) > 0 ? t('aw_fad_rate_life', { rate: short(r.rate), life: short(r.usefulLifeYears) }) : '—'}</td>
                          <td style={num} data-c="open">{money(r.openingAccum)}</td>
                          <td style={{ ...num, fontWeight: 700 }} data-c="period"
                            title={r.days ? t('aw_fad_days_title', { n: r.days, from: dmy(r.from), to: dmy(r.to) }) : undefined}>
                            {money(r.periodCharge)}
                          </td>
                          <td style={num} data-c="close">{money(r.closingAccum)}</td>
                          <td style={num} data-c="nbv">{money(r.closingNbv)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr data-fad-total>
                  <td colSpan={3} style={{ ...td, fontWeight: 800, borderTop: '2px solid var(--border-strong)' }}>{t('aw_fad_total', { n: rows.length })}</td>
                  <td style={{ ...num, fontWeight: 800, borderTop: '2px solid var(--border-strong)' }} data-c="cost">{money(totals.cost)}</td>
                  <td style={{ ...num, borderTop: '2px solid var(--border-strong)' }} />
                  <td style={{ ...num, fontWeight: 800, borderTop: '2px solid var(--border-strong)' }} data-c="open">{money(totals.open)}</td>
                  <td style={{ ...num, fontWeight: 800, borderTop: '2px solid var(--border-strong)' }} data-c="period">{money(totals.period)}</td>
                  <td style={{ ...num, fontWeight: 800, borderTop: '2px solid var(--border-strong)' }} data-c="close">{money(totals.close)}</td>
                  <td style={{ ...num, fontWeight: 800, borderTop: '2px solid var(--border-strong)' }} data-c="nbv">{money(totals.nbv)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
        <Pagination page={pg} total={flat.length} limit={limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />
      </div>
    </>
  );
}

/** Every run of the period, with its reversal — the "complete history" (COA-H8 / H10), and for a writer
 *  "Reverse & recalculate" (COA-H10.3) on a POSTED run nothing has taken back yet.
 *
 *  ⛔ The reversal is the journal's own (POST /journal/:id/reverse raises it as a draft, POST
 *  /journal/:id/post posts it): no second reversal is written here. Raising it alone would not do —
 *  the register is given back only when the reversal is POSTED (journalPost giveBackToRegister), and
 *  until then the window still reads as charged — so the action is both calls, under one question that
 *  says the reversal will be made AND posted (since batch 11 R2 the shared reason dialog, reverseReason.tsx,
 *  because the route raises no reversal without a reason). A reversal that was raised but not posted (refused
 *  at posting, or raised in Journal entries) leaves the run offering to post it, so a half-done
 *  reversal is never stranded here. Read-only seats and a closed period (canWrite false) get no button
 *  at all.
 *
 *  ⛔ NO BUTTON THE SERVER WILL REFUSE (review b9 #6): a refused POST is a 409 JSON, and every one pages
 *  the admins. A refusal the books already decide when the list is read (the run's period closed, an
 *  asset of the run under a disposal — draft or posted — that counts the charge) comes WITH the list,
 *  as r.refusal: a code and the numbers it names, worded here in the reader's language where the
 *  button would be. Only a refusal the list could not know of (a disposal made since it was read) is
 *  the server's sentence, shown as it came — and the list is read again, so the row then says why
 *  instead of offering the same refusal twice. ⛔ Until that list has ARRIVED every run's button is
 *  held (drawn, not pressable): the list still on the screen is the one that offered them, and a press
 *  in that gap would send a certain 409 — a second page to the admins. Not the refused run's alone: a
 *  disposal refuses EVERY run that charged its asset, whichever window, so a sibling pressed in that
 *  gap is refused just the same (review b9 #6, second residue). */
function RunHistory({ ctx, t, canWrite, onReversed }: { ctx: AwCtx; t: TF; canWrite: boolean; onReversed: (r: FaRun, rvNo: string) => void }) {
  const [runs, setRuns] = useState<FaRun[] | null>(null);
  const [err, setErr] = useState('');
  const [entry, setEntry] = useState('');
  const [revBusy, setRevBusy] = useState('');
  const [revErr, setRevErr] = useState('');
  // The run whose reason is being asked for (batch 11 R2): a reversal is raised only with one.
  const [asking, setAsking] = useState<FaRun | null>(null);
  // Bumped to read the list again after a refusal that raised nothing (so the shell's panes need not).
  const [reread, setReread] = useState(0);
  // Set when a reverse/post was refused, to the number of the last list read STARTED before that
  // refusal: only the answer of a read started after it lets the list go. The whole list, not the
  // refused run — see above. Every read is numbered, whichever change asked for it (this list's own
  // re-read, or the shell's reload after a reversal was raised and its posting refused) — an answer
  // read before the refusal proves nothing.
  const [heldAt, setHeldAt] = useState<number | null>(null);
  const reads = useRef(0);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    let alive = true;
    const my = ++reads.current;
    const letGo = () => setHeldAt(at => (at !== null && at < my ? null : at));
    api.faRuns(ctx.clientId, ctx.periodId)
      .then((d: { runs: FaRun[] }) => { if (alive) { setRuns(d.runs || []); setErr(''); letGo(); } })
      .catch((e: any) => { if (alive) { setRuns([]); setErr(String(e?.message || e)); letGo(); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.periodId, ctx.reload, reread]);
  const word = (s: string) => (RUN_STATUS_KEY[s] ? t(RUN_STATUS_KEY[s]) : s);
  /** Where the button would be: a writer, a posted run, not yet reversed by a POSTED entry. */
  const offered = (r: FaRun) => canWrite && r.status === 'posted' && (!r.reversedBy || r.reversedBy.status !== 'posted');
  /** …and whether it is drawn there: only when the server has not already said it would refuse. */
  const reversible = (r: FaRun) => offered(r) && !r.refusal;
  /** A run was refused since this list was read, and the list that says why is not here yet. */
  const held = heldAt !== null;
  const refusalText = (r: FaRun) => {
    const f = r.refusal!;
    return t(REFUSAL_KEY[f.code] || 'aw_fad_reverse_refused', { no: r.docNo, doc: f.docNo, asset: f.assetNo, from: f.from ? dmy(f.from) : '', to: f.to ? dmy(f.to) : '' });
  };
  // ⛔ Batch 11 R2: raising a reversal needs a REASON (the Journal route refuses one without), so with no draft
  // reversal the run's confirm became the shared reason dialog — the same sentence as its line, "Reverse and post" as
  // its button — and both calls run from its submit. Posting a draft reversal raised elsewhere raises nothing, so it
  // keeps its confirm and asks no reason. Either way `run` below is what it always was.
  const reverse = (r: FaRun) => {
    const draft = r.reversedBy && r.reversedBy.status !== 'posted' ? r.reversedBy : null;
    if (!draft) { setRevErr(''); setAsking(r); return; }
    if (!window.confirm(t('aw_fad_reverse_confirm_draft', { no: r.docNo, rv: draft.docNo }))) return;
    void run(r, draft, '');
  };
  const run = async (r: FaRun, draft: { entryId: string; docNo: string } | null, reason: string) => {
    setRevBusy(r.entryId); setRevErr('');
    let raised = false;
    try {
      const rv: { entryId: string; docNo: string } = draft || await api.jnlReverse(r.entryId, reason);
      raised = !draft;
      await api.jnlPost(rv.entryId);
      // The parent switches to the table (this list unmounts) and calculates the window again.
      onReversed(r, rv.docNo);
    } catch (e: any) {
      // ⛔ Held first, in the same batch as the re-read below, so no render between them draws the
      // button pressable again (review b9 #6 residue).
      if (mounted.current) { setRevErr(String(e?.message || e)); setHeldAt(reads.current); }
      // Raised but refused at posting: the reversal is a draft now, so the list (and every pane) re-reads
      // and this row says so — and still offers to post it. Refused before anything was raised (the
      // books changed since the list was read): the list alone is read again, and the row says why in
      // place of the button, which is not offered to the same refusal a second time. Either way every
      // button stays held until that list is in.
      if (raised) ctx.bump();
      else if (mounted.current) setReread(n => n + 1);
    } finally {
      if (mounted.current) setRevBusy('');
    }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {revErr && <div role="alert" style={errBox} data-fad-reverse-err>{revErr}</div>}
      <div style={{ ...card, padding: 0, overflow: 'visible' }}>
        {err && <div style={{ ...errBox, margin: 12 }}>{err}</div>}
        <div style={{ overflowX: 'auto' }}>
          {runs === null ? <Loader /> : !runs.length ? <div style={emptyLine}>{t('aw_fad_hist_none')}</div> : (
            <table style={table} data-fad-runs>
              <thead>
                <tr>
                  <th style={th}>{t('aw_fad_hist_col_doc')}</th>
                  <th style={th}>{t('aw_fad_hist_col_window')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('aw_fad_hist_col_assets')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>{t('aw_fad_hist_col_amount')}</th>
                  <th style={th}>{t('aw_fad_hist_col_status')}</th>
                  <th style={th}>{t('aw_fad_hist_col_reversal')}</th>
                </tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <tr key={r.entryId} data-fad-run={r.entryId}>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button type="button" onClick={() => setEntry(r.entryId)} title={t('aw_fad_open_entry', { no: r.docNo })}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontWeight: 700, color: 'var(--accent)', fontVariantNumeric: 'tabular-nums' }}>
                        {r.docNo}
                      </button>
                    </td>
                    {/* A posted reversal takes the run's charge rows back, and with them the window's
                        first day and the asset count — the entry keeps its date and its amount. */}
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.windowFrom ? `${dmy(r.windowFrom)} – ${dmy(r.windowTo)}` : t('aw_fad_window_to', { to: dmy(r.windowTo) })}</td>
                    <td style={num}>{Number(r.assets) > 0 ? r.assets : '—'}</td>
                    <td style={{ ...num, fontWeight: 700 }}>{baht(r.amount)}</td>
                    <td style={td}><span style={runPill(r.status)}>{word(r.status)}</span></td>
                    <td style={td}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                        {r.reversedBy ? (
                          <button type="button" onClick={() => setEntry(r.reversedBy!.entryId)} title={t('aw_fad_open_entry', { no: r.reversedBy.docNo })} data-fad-reversed
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', color: 'var(--text-secondary)', textAlign: 'left' }}>
                            {t('aw_fad_reversed_by', { no: r.reversedBy.docNo, status: word(r.reversedBy.status) })}
                          </button>
                        ) : !offered(r) && <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                        {offered(r) && r.refusal && (
                          <span data-fad-reverse-refused={r.refusal.code} style={{ color: 'var(--text-secondary)', fontSize: 12, maxWidth: 340 }}>
                            <i className="ti ti-lock" aria-hidden /> {refusalText(r)}
                          </span>
                        )}
                        {reversible(r) && (
                          <button type="button" onClick={() => { void reverse(r); }} disabled={!!revBusy || held} title={t('aw_fad_reverse_title')} data-fad-reverse={r.entryId}
                            style={{ ...actBtn, whiteSpace: 'nowrap', opacity: revBusy || held ? 0.5 : 1 }}>
                            <i className={revBusy === r.entryId || held ? 'ti ti-loader' : 'ti ti-arrow-back-up'} aria-hidden /> {t('aw_fad_reverse')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <div style={note}><i className="ti ti-info-circle" aria-hidden /> {t('aw_fad_hist_note')}</div>
      {entry && <EntryDetailDialog entryId={entry} onClose={() => setEntry('')} />}
      {/* The dialog stays up while both calls run, then closes whatever they answered: a refusal is shown above
          the list (revErr) with the hold that follows it, exactly as before — never left in a dialog whose second
          press could only raise a reversal that already exists. */}
      {asking && (
        <ReverseReasonDialog docNo={asking.docNo} line={t('aw_fad_reverse_confirm', { no: asking.docNo })} submitLabel={t('aw_rvr_submit_post')}
          onClose={() => setAsking(null)}
          onSubmit={async reason => { const r = asking; await run(r, null, reason); if (mounted.current) setAsking(null); }} />
      )}
    </div>
  );
}
